import { useRef } from 'react';

import useDisabledButtonWithDebounce from '@/features/v2/hooks/useDissabledButtonWithDebounce';

import { FORM_DATA_KEY_FACTORY, useFormDataQuery } from '@/api/getters';
import {
  useFormMutation,
  useSubmitStagesMutation,
  useSubmitStepMutation,
} from '@/api/setters';
import { Steps } from '@/api/types';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import { PRODUCT } from '@/consts';
import { formMutationDto } from '@/dto/formMutationDto';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';
import { queryClient } from '@/lib/query';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export type SubmitButton = {
  label?: string;
  hidden?: boolean;
  isDisabledProp?: boolean;
  nextStageOnSubmit?: boolean;
  isLoading?: boolean;
  uniqTestId?: string;
};
export const SubmitStageButtonNew = ({
  hidden,
  label,
  isDisabledProp,
  nextStageOnSubmit = true,
  isLoading,
  uniqTestId,
}: SubmitButton) => {
  const { i18n } = useLingui();
  const { id: hash } = useParams();

  const methods = useFormContext();
  const formMutation = useFormMutation();

  const {
    step,
    innerStep,
    innerObject,
    currentStep,
    forceDisableButton,
    nextStep,
    isDeadEnd,
    setStepTag,
  } = useStepper();

  const submitStages = useSubmitStagesMutation({
    withoutInvalidate: isDeadEnd ? false : true,
  });

  const submitStep = useSubmitStepMutation();

  const { additionalInfo } = useAdditionalInfoContext();

  // Guards against a duplicate complete-stage call (it was firing twice on
  // household and pushing stage_III straight to stage_III_finished).
  const isCompletingRef = useRef(false);

  const { data, isFetching } = useFormDataQuery();

  const completeValidation = !!currentStep?.completeValidation?.(innerStep);

  const isPending =
    formMutation.isPending || submitStages.isPending || isFetching;

  const { isDisabled } = useDisabledButtonWithDebounce();

  const buttonDisabled = completeValidation
    ? false || isDisabledProp
    : isDisabled || forceDisableButton || isDisabledProp;

  const handleSubmit = async () => {
    // Household had a bug where complete-stage fired twice, jumping stage_III
    // straight to stage_III_finished while the stepper was still at the start of
    // the documents step (step 2, innerStep 0). Bail out and re-sync with the
    // server if a completion is already in flight, or if we're being asked to
    // complete a stage from the first inner step of a step that still has inner
    // steps ahead (and it isn't a dead-end shortcut).
    if (data?.product.id === PRODUCT.HOUSE_HOLD) {
      const stepLastStep =
        currentStep?.lastStep?.(methods.watch(), additionalInfo)?.lastStep ?? 0;
      const isPrematureCompletion =
        innerStep === 0 && stepLastStep > 0 && !isDeadEnd;

      if (isCompletingRef.current || isPrematureCompletion) {
        window.location.reload();
        return;
      }

      isCompletingRef.current = true;
    }

    const isValid = await methods.trigger();
    if (!isValid) {
      isCompletingRef.current = false;
      return;
    }

    if (!data || !nextStageOnSubmit) {
      isCompletingRef.current = false;
      return;
    }

    try {
      if (isDeadEnd) {
        await formMutation.mutateAsync({
          ...formMutationDto({
            form_data: methods.watch(),
            stepper: {
              step: step + 1,
              innerStep: { ...innerObject, [step + 1]: 0 },
            },
          }),
        });
        await submitStages.mutateAsync(data.stage);
        submitStep
          .mutateAsync(`step_${data.stage.split('_')[1]}_finish` as Steps)
          .then(() => {
            setStepTag(`step_${data.stage.split('_')[1]}_finish` as Steps);
          });
        return;
      }

      await formMutation.mutateAsync({
        ...formMutationDto({
          form_data: methods.watch(),
          stepper: {
            step: step + 1,
            innerStep: { ...innerObject, [step + 1]: 0 },
          },
        }),
      });

      submitStep
        .mutateAsync(`step_${data.stage.split('_')[1]}_finish` as Steps)
        .then(() => {
          setStepTag(`step_${data.stage.split('_')[1]}_finish` as Steps);
        });
      await submitStages.mutateAsync(data.stage, {
        onSuccess: () => {
          nextStep();
          methods.reset(methods.getValues(), {
            keepValues: true,
          });
          scrollToTop();
          queryClient.invalidateQueries({
            queryKey: FORM_DATA_KEY_FACTORY(hash),
          });
        },
      });
    } finally {
      isCompletingRef.current = false;
    }
  };

  if (hidden) {
    return null;
  }

  return (
    <Button
      type={'submit'}
      onClick={handleSubmit}
      className={'w-full md:w-auto'}
      loading={isPending}
      disabled={buttonDisabled || isPending || isLoading}
      data-testid={
        uniqTestId ? `submit-stage-button-${uniqTestId}` : 'submit-stage-button'
      }
    >
      {label || i18n.t('Zakończ formularz')}
      <ButtonLoading />
      <ButtonRightArrow />
    </Button>
  );
};
